import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
} from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { OnInit, AfterViewInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { Observable, catchError, concatAll, map, of } from 'rxjs';
import { environment } from '@environments/environment';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { LayoutService, PageLayout } from '@app/layout/layout.service';
import { isNullOrUndefined } from '@app/shared/utils';
import { AccountService } from '@app/services/account.service';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  BaseAuthenticatedService,
  httpQueryParams,
} from '@app/services/base-authenticated.service';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

// Add interface for the invitation response
interface InvitationResponse {
  status: string;
  email: string;
  organization_id: number;
  organization_name: string;
}

interface InvitationError {
  error: string;
}

@Component({
  selector: 'izy-accept-invitation',
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    MatChipsModule,
    MatCardModule,
    MatProgressSpinnerModule,
  ],
  providers: [AccountService],
  standalone: true,
  templateUrl: './accept-invitation.component.html',
  styleUrl: './accept-invitation.component.scss',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class AcceptInvitationComponent implements OnInit {
  invitation: InvitationResponse | null = null;
  error: string = '';
  isLoading: boolean = false;
  uuid: string | null = null; // Store the UUID
  cdr = inject(ChangeDetectorRef);

  user: any | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private httpClient: HttpClient,
    private baseAuthenticatedService: BaseAuthenticatedService,
    private auth: AngularFireAuth,
    private accountService: AccountService,
    private layoutService: LayoutService
  ) {
    // this.invitation$ = of(null);
  }

  getTranslatedStatus(status: string): string {
    const statusMap: { [key: string]: string } = {
      PENDING: 'In sospeso',
      EXPIRED: 'Scaduto',
      ACCEPTED: 'Accettato',
      DECLINED: 'Rifiutato',
    };
    return statusMap[status] || status;
  }

  ngAfterViewInit() {
    // FIXME: Why is this detached?
    console.log('Is detached?', (this.cdr as any)._view?.[3]?.flags?.Detached);
    this.cdr.reattach();

    this.layoutService.setLayout(PageLayout.UnAuthorized);
    this.cdr.markForCheck();
  }

  copyOfProtectedPost<T>(
    path: string,
    params: httpQueryParams = {},
    data: { [key: string]: any }
  ): Observable<T> {
    let url = this.baseAuthenticatedService.getUrl(path);

    return this.baseAuthenticatedService.getOptions(params).pipe(
      map((option) => {
        return this.httpClient.post<T>(url, data, option);
      }),
      concatAll()
    );
    // let option = this.getOptions(params);
    // return this.httpClient.get<T>(url, option);
  }

  getUserData(retry: number = 0) {
    this.accountService.getUserDetail().subscribe({
      next: (djUser) => {
        if (djUser) {
          this.user = djUser;
        } else {
          this.user = null;
          console.error('User not found');
        }
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        if (retry < 3) {
          console.log('Retrying to fetch user data, attempt', retry + 1);
          // the first time login need some time to save the user data in the backend
          setTimeout(() => {
            this.getUserData(retry + 1);
          }, 1000);
          return;
        }
        this.user = null;
        this.isLoading = false;
        this.cdr.markForCheck();
        console.error('Error fetching user details:', err);
        // this.router.navigate(['/auth/logout']);
      },
    });
  }

  checkFirebaseUserLogin() {
    this.auth.user.subscribe({
      next: (currentUser) => {
        if (isNullOrUndefined(currentUser)) {
          this.user = null;
        } else {
          this.getUserData();
          // User is authenticated, set the layout to Authorized

          // let pageL: PageLayout = PageLayout.Authorized;
          // this.layoutService.setLayout(pageL);
          // await pageL = lastValueFrom(this.layoutService.layout$);
          // if (pageL === PageLayout.Authorized) {
          //   this.router.navigate(['/', 'dashboard']);
          //   return;
          // }
        }
      },
      error: (error) => {
        console.error('Error fetching current user:', error);
        this.isLoading = false;
        this.cdr.markForCheck();
        // this.router.navigate(['/auth/login']);
      },
    });
  }

  ngOnInit(): void {
    this.layoutService.setLayout(PageLayout.UnAuthorized);
    this.cdr.markForCheck();
    const strategy = (this.constructor as any).ɵcmp?.changeDetection;
    console.log(
      'Change detection strategy:',
      strategy === ChangeDetectionStrategy.OnPush ? 'OnPush' : 'Default'
    );

    this.uuid = this.route.snapshot.paramMap.get('uuid');
    console.log('uuid', this.uuid);
    this.checkFirebaseUserLogin();

    if (this.uuid) {
      this.loadInvitation(this.uuid);
    } else {
      this.error = 'No invitation UUID provided';
    }
  }

  loadInvitation(uuid: string): void {
    this.isLoading = true;
    this.error = '';
    this.cdr.markForCheck();

    this.httpClient
      .get<InvitationResponse>(
        `${environment.backendDomain}/api/invitation/${uuid}/`
      )
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          console.log('Invitation details', response);
          this.invitation = response;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isLoading = false;
          if (error.error?.error) {
            this.error = error.error.error;
          } else {
            this.error = 'Errore nel caricamento degli inviti.';
          }
          this.cdr.markForCheck();
          return of(null);
        },
      });
  }

  // method to accept the invitation, uses the stored UUID
  acceptInvitation(): void {
    if (!this.uuid) {
      this.error = 'Errore, nessun invito trovato.';
      return;
    }

    this.isLoading = true;
    this.copyOfProtectedPost(
      `api/invitation-action/${this.uuid}/accept/`,
      {},
      {}
    ).subscribe({
      next: (response) => {
        this.isLoading = false;
        console.log('Invitation accepted', response);
        // Redirect to organization page or success page
        this.router.navigate(['/home-redirect']);
      },
      error: (error) => {
        this.isLoading = false;
        if (error.error?.error) {
          this.error = error.error.error;
        } else {
          this.error = "Errore durante l'operazione.";
        }
      },
    });
  }

  declineInvitation(): void {
    if (!this.uuid) {
      this.error = 'Errore, nessun invito trovato.';
      return;
    }

    this.isLoading = true;
    this.copyOfProtectedPost(
      `api/invitation-action/${this.uuid}/decline/`,
      {},
      {}
    ).subscribe({
      next: (response) => {
        this.isLoading = false;
        console.log('Invitation declined', response);
        // Redirect to organization page or success page
        this.router.navigate(['/home-redirect']);
      },
      error: (error) => {
        this.isLoading = false;
        if (error.error?.error) {
          this.error = error.error.error;
        } else {
          this.error = "Errore durante l'operazione.";
        }
      },
    });
  }

  navigateToHome() {
    const org_id = this.route.snapshot.paramMap.get('organizationId');
    this.router.navigate([`/${org_id}/hme-redirect`]);
  }
}
